import {
  OrderStatus,
  OrderStatusChangeReason,
  OrderStatusEvent,
} from './order-status.model';

/**
 * Why an order stopped, in the customer's words (#11398, #11663); the
 * `cancelled…` ones say it was cancelled, so they go with Cancelled only.
 */
export type OrderStatusReason =
  | 'cancelledPaymentDeadline'
  | 'cancelledPaymentFailed'
  | 'cancelledCourierNotFound'
  | 'cancelledItemOutOfStock'
  | 'cancelledItemQuality'
  | 'cancelledCustomerRequest'
  | 'cancelledOrderChanged'
  | 'cancelledByCustomer'
  | 'cancelledByStore'
  | 'paymentDeadline'
  | 'paymentCancelledByCustomer'
  | 'paymentFailed'
  | 'courierNotFound';

/**
 * The reason for the latest change into the stop the order is in, worded for
 * that stop; null for any other status, a plain move, or a reason the
 * customer needn't read. Staff's note never reaches them.
 */
export function getOrderStatusReason(
  orderStatus: OrderStatus,
  orderStatusEvents: readonly OrderStatusEvent[],
): OrderStatusReason | null {
  const latestChangeInto = orderStatusEvents
    .filter((orderStatusEvent) => orderStatusEvent.status_after === orderStatus)
    .reduce<OrderStatusEvent | null>(
      (latest, orderStatusEvent) =>
        !latest ||
        orderStatusEvent.date_created_timestamp > latest.date_created_timestamp
          ? orderStatusEvent
          : latest,
      null,
    );
  if (!latestChangeInto || !latestChangeInto.reason) {
    return null;
  }
  const reason = latestChangeInto.reason;
  switch (orderStatus) {
    case 'Cancelled':
      return getCancelledReason(reason);
    case 'Payment failure':
      return getPaymentFailureReason(reason);
    case 'Shipping failure':
      return getShippingFailureReason(reason);
    case 'Ready for payment':
    case 'Awaiting payment':
    case 'Paid':
    case 'Ready for shipping':
    case 'Awaiting shipping':
    case 'In shipping':
    case 'Complete':
      return null;
  }
}

/**
 * Whether staff's timeline prints a change's reason: not for the system ones
 * its step already says (#11905).
 */
export function isOrderStatusChangeReasonShown(
  reason: OrderStatusChangeReason,
): boolean {
  switch (reason) {
    case 'requesting_payment':
    case 'payment_retried_by_customer':
    case 'offline_sales_auto_completion':
      return false;
    case 'item_out_of_stock':
    case 'item_quality':
    case 'payment_failed':
    case 'customer_request':
    case 'order_changed':
    case 'courier_not_found':
    case 'other':
    case 'payment_deadline_exceeded':
    case 'cancelled_by_customer':
    case 'payment_cancelled_by_customer':
      return true;
  }
}

function getCancelledReason(
  reason: OrderStatusChangeReason,
): OrderStatusReason | null {
  switch (reason) {
    case 'payment_deadline_exceeded':
      return 'cancelledPaymentDeadline';
    case 'payment_failed':
      return 'cancelledPaymentFailed';
    case 'courier_not_found':
      return 'cancelledCourierNotFound';
    case 'item_out_of_stock':
      return 'cancelledItemOutOfStock';
    case 'item_quality':
      return 'cancelledItemQuality';
    case 'customer_request':
      return 'cancelledCustomerRequest';
    case 'order_changed':
      return 'cancelledOrderChanged';
    case 'cancelled_by_customer':
      return 'cancelledByCustomer';
    case 'other':
      return 'cancelledByStore';
    case 'payment_cancelled_by_customer':
    case 'requesting_payment':
    case 'payment_retried_by_customer':
    case 'offline_sales_auto_completion':
      return null;
  }
}

function getPaymentFailureReason(
  reason: OrderStatusChangeReason,
): OrderStatusReason | null {
  switch (reason) {
    case 'payment_deadline_exceeded':
      return 'paymentDeadline';
    case 'payment_cancelled_by_customer':
      return 'paymentCancelledByCustomer';
    case 'payment_failed':
      return 'paymentFailed';
    case 'courier_not_found':
    case 'item_out_of_stock':
    case 'item_quality':
    case 'customer_request':
    case 'order_changed':
    case 'cancelled_by_customer':
    case 'other':
    case 'requesting_payment':
    case 'payment_retried_by_customer':
    case 'offline_sales_auto_completion':
      return null;
  }
}

function getShippingFailureReason(
  reason: OrderStatusChangeReason,
): OrderStatusReason | null {
  switch (reason) {
    case 'courier_not_found':
      return 'courierNotFound';
    case 'payment_deadline_exceeded':
    case 'payment_cancelled_by_customer':
    case 'payment_failed':
    case 'item_out_of_stock':
    case 'item_quality':
    case 'customer_request':
    case 'order_changed':
    case 'cancelled_by_customer':
    case 'other':
    case 'requesting_payment':
    case 'payment_retried_by_customer':
    case 'offline_sales_auto_completion':
      return null;
  }
}
